import React, { useState, useEffect, useContext } from "react";
import {
  Plus,
  Trash2,
  ChevronDown,
  Save,
  X,
  AlertCircle,
  CheckCircle,
  Eye,
  Users,
} from "lucide-react";
import { ThemeContext } from "./theme";
import { LanguageContext } from "./LanguageContext";
import ReusableDropdown from "../components/ReusableDropdown";
import AlertModal from "../components/AlertModal";
// ✅ Import apiFetch for automatic token refresh
import { apiFetch } from "../context/AuthContext";
import { useProfile } from "../hooks/useProfile";

const AdminLevelConfigurationPage = () => {
  const { theme: currentTheme } = useContext(ThemeContext);
  const theme = currentTheme === "dark" ? "dark" : "light";

  // Use global LanguageContext instead of local state
  const { language } = useContext(LanguageContext);

  // Profile from /users/me
  const { profile } = useProfile();

  // State for admin levels
  const [adminLevels, setAdminLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState({ client: "", company: "" });

  // State for companies dropdown
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [companySearchValue, setCompanySearchValue] = useState("");

  // State for delete confirmation modal
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingLevelId, setDeletingLevelId] = useState(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [expandedLevel, setExpandedLevel] = useState(null);
  const [selectedLevelForModule, setSelectedLevelForModule] = useState(null);
  const [selectedModuleForFeature, setSelectedModuleForFeature] = useState(null);
  const [selectedModuleName, setSelectedModuleName] = useState("");
  const [selectedFeatureName, setSelectedFeatureName] = useState("");

  // Visibility control state
  const [selectedLevelForVisibility, setSelectedLevelForVisibility] = useState(null);
  const [visibilityRulesLoading, setVisibilityRulesLoading] = useState(false);
  const [selectedVisibilityCompanies, setSelectedVisibilityCompanies] = useState([]);
  const [departmentsByCompany, setDepartmentsByCompany] = useState({});
  const [selectedDepartmentsByCompany, setSelectedDepartmentsByCompany] = useState({});
  const [usersByCompany, setUsersByCompany] = useState({});
  const [selectedUsersByCompany, setSelectedUsersByCompany] = useState({});
  const [visibilitySearch, setVisibilitySearch] = useState("");

  // Assign employees state
  const [selectedLevelForAssign, setSelectedLevelForAssign] = useState(null);
  const [assignCandidates, setAssignCandidates] = useState([]);
  const [assignSelectedUserIds, setAssignSelectedUserIds] = useState([]);
  const [assignSearch, setAssignSearch] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    clientName: "",
    workingCompany: "",
  });

  // API Base URL
  const API_BASE_URL = `${process.env.REACT_APP_BASE_URL}/api`;

  // Translations
  const translations = {
    ar: {
      selectAll: "تحديد الكل",
      pageTitle: "إدارة المستويات الإدارية",
      createAdminLevel: "إنشاء مستوى إداري",
      selectCompany: "اختر الشركة",
      clientName: "اسم العميل",
      workingCompany: "الشركة العاملة",
      selectClient: "اختر العميل...",
      selectCompany2: "اختر الشركة...",
      loadAdminLevels: "تحميل المستويات الإدارية",
      loadingCompanies: "جاري تحميل الشركات...",
      adminLevels: "المستويات الإدارية",
      loading: "جاري التحميل...",
      noAdminLevels: "لا توجد مستويات إدارية للشركة المحددة",
      visibilityControl: "التحكم في الرؤية",
      assignEmployees: "تعيين الموظفين",
      modulesFeatures: "الوحدات والميزات",
      addModule: "إضافة وحدة",
      noModules: "لا توجد وحدات معينة",
      addFeature: "إضافة ميزة",
      noFeatures: "لا توجد ميزات مكونة",
      name: "الاسم",
      description: "الوصف",
      create: "إنشاء",
      cancel: "إلغاء",
      save: "حفظ",
      selectModule: "اختر وحدة",
      selectModulePlaceholder: "-- اختر وحدة --",
      selectFeature: "اختر ميزة",
      selectFeaturePlaceholder: "-- اختر ميزة --",
      visibilityControlTitle: "التحكم في الرؤية",
      visibilityDesc: "تكوين من يمكن لهذا المستوى الإداري مشاهدته (الشركات ← الأقسام ← المستخدمين).",
      searchUsers: "البحث عن المستخدمين...",
      loadingVisibility: "جاري تحميل قواعد الرؤية...",
      companies: "1) الشركات",
      noCompanies: "لا توجد شركات لهذا العميل.",
      departments: "2) الأقسام (نوع الموظف)",
      selectAtLeastOne: "اختر شركة واحدة على الأقل.",
      noDepartments: "لم يتم تحميل أي أقسام.",
      departmentTip: "تلميح: إذا اخترت أقساماً لشركة، فهذا يعني 'جميع المستخدمين في تلك الأقسام'.",
      users: "3) المستخدمون (اختياري)",
      usersTip: "إذا كنت تريد مستخدمين محددين فقط، اترك الأقسام غير محددة واختر المستخدمين.",
      noUsers: "لم يتم العثور على مستخدمين للاختيار الحالي.",
      assignToLevel: "تعيين موظفين للمستوى",
      assignDesc: "سيؤدي اختيار المستخدمين هنا إلى تعيين مستواهم الإداري إلى",
      searchByName: "البحث بالاسم/البريد/القسم...",
      loadingUsers: "جاري تحميل المستخدمين...",
      saveAssignments: "حفظ التعيينات",
      deleteConfirm: "هل أنت متأكد من حذف هذا المستوى الإداري؟",
      created: "تم إنشاء المستوى الإداري بنجاح!",
      moduleUpdated: "تم تحديث صلاحية الوحدة!",
      featureUpdated: "تم تحديث صلاحية الميزة!",
      deleted: "تم حذف المستوى الإداري بنجاح!",
      moduleAdded: "تم إضافة الوحدة بنجاح!",
      featureAdded: "تم إضافة الميزة بنجاح!",
      visibilitySaved: "تم حفظ قواعد الرؤية!",
      assignmentsSaved: "تم تعيين المستخدمين للمستوى!",
      selectModuleError: "الرجاء اختيار وحدة",
      selectFeatureError: "الرجاء اختيار ميزة",
      namePlaceholder: "مثال: مدير الموارد البشرية",
      descPlaceholder: "وصف مختصر لهذا المستوى الإداري",
      employeeManagementDesc: "إدارة بيانات الموظفين الأساسية (view, create, edit, delete, export, import)",
      changesPageDesc: "إدارة تغييرات دورة حياة الموظفين (ترقيات، تحويلات، إنهاءات، إلخ) - مستقلة تماماً عن إدارة الموظفين",
    },
    en: {
      selectAll: "Select all",
      pageTitle: "Admin Levels Management",
      createAdminLevel: "Create Admin Level",
      selectCompany: "Select Company",
      clientName: "Client Name",
      workingCompany: "Working Company",
      selectClient: "Select client...",
      selectCompany2: "Select company...",
      loadAdminLevels: "Load Admin Levels",
      loadingCompanies: "Loading companies...",
      adminLevels: "Admin Levels",
      loading: "Loading...",
      noAdminLevels: "No admin levels found for selected company",
      visibilityControl: "Visibility Control",
      assignEmployees: "Assign Employees",
      modulesFeatures: "Modules & Features",
      addModule: "Add Module",
      noModules: "No modules assigned",
      addFeature: "Add Feature",
      noFeatures: "No features configured",
      name: "Name",
      description: "Description",
      create: "Create",
      cancel: "Cancel",
      save: "Save",
      selectModule: "Select Module",
      selectModulePlaceholder: "-- Select a module --",
      selectFeature: "Select Feature",
      selectFeaturePlaceholder: "-- Select a feature --",
      visibilityControlTitle: "Visibility Control",
      visibilityDesc: "Configure who this admin level can view (companies → departments → users).",
      searchUsers: "Search users...",
      loadingVisibility: "Loading visibility rules...",
      companies: "1) Companies",
      noCompanies: "No companies found for this client.",
      departments: "2) Departments (Employee Type)",
      selectAtLeastOne: "Select at least one company.",
      noDepartments: "No departments loaded.",
      departmentTip: "Tip: If you select departments for a company, it means 'all users in those departments'.",
      users: "3) Users (Optional)",
      usersTip: "If you want specific users only, leave departments unchecked and select users.",
      noUsers: "No users found for current selection.",
      assignToLevel: "Assign Employees to Level",
      assignDesc: "Selecting users here will set their admin level to",
      searchByName: "Search by name/email/department...",
      loadingUsers: "Loading users...",
      saveAssignments: "Save Assignments",
      deleteConfirm: "Are you sure you want to delete this admin level?",
      created: "Admin level created successfully!",
      moduleUpdated: "Module permission updated!",
      featureUpdated: "Feature permission updated!",
      deleted: "Admin level deleted successfully!",
      moduleAdded: "Module added successfully!",
      featureAdded: "Feature added successfully!",
      visibilitySaved: "Visibility rules saved!",
      assignmentsSaved: "Users assigned to level!",
      selectModuleError: "Please select a module",
      selectFeatureError: "Please select a feature",
      namePlaceholder: "e.g., HR Manager",
      descPlaceholder: "Brief description of this admin level",
      employeeManagementDesc: "Core employee data management (view, create, edit, delete, export, import)",
      changesPageDesc: "Manage employee lifecycle changes (promotions, transfers, terminations, etc.) - completely separate from employee management",
    }
  };

  const t = translations[language];

  // Feature label mapping for better UI display
  const getFeatureLabel = (featureName) => {
    const featureLabels = {
      en: {
        // Basic Access
        "viewConfigurations": "View Configurations",
        "editCompanyInfo": "Edit Company Information",
        "add": "Create",
        "remove": "Delete",

        // Attendance & Working Hours
        "editWorkSchedule": "Edit Work Schedule",
        "editLateRules": "Edit Late Arrival Rules",
        "editAbsenceRules": "Edit Absence Rules",
        "editGracePeriods": "Edit Grace Periods",

        // Overtime Management
        "enableOvertime": "Enable/Disable Overtime",
        "editOvertimeRate": "Edit Overtime Rate",
        "editOvertimeLimits": "Edit Overtime Limits",
        "editOvertimeApproval": "Edit Overtime Approval",

        // Leave Policies
        "editAnnualLeaveProgression": "Edit Annual Leave Progression",
        "editSickLeaveQuota": "Edit Sick Leave Quota",
        "leaveRules": "Leave Rules",
        "editCasualLeaveQuota": "Edit Casual Leave Quota",
        "editLeaveApprovalRules": "Edit Leave Approval Rules",
        "editLeaveCarryover": "Edit Leave Carryover",

        // Rewards & Bonuses
        "enablePerformanceBonus": "Enable Performance Bonus",
        "enableAttendanceBonus": "Enable Attendance Bonus",
        "editBonusAmounts": "Edit Bonus Amounts",
        "editBonusCriteria": "Edit Bonus Criteria",

        // System Features
        "enablePrivateMessaging": "Enable Private Messaging",
        "enableDocumentSharing": "Enable Document Sharing",
        "enablePerformanceReviews": "Enable Performance Reviews",
        "enableSalarySlips": "Enable Salary Slips",

        // Salary Advancements
        "enableAdvancements": "Enable Salary Advancements",
        "editAdvancementLimits": "Edit Advancement Limits",
        "advancementRules": "Advancement Rules",
        "editAdvancementPercentage": "Edit Advancement Percentage",
        "editAdvancementApproval": "Edit Advancement Approval",
        "editRepaymentTerms": "Edit Repayment Terms",

        // Advanced Operations
        "detectCompanies": "Detect Companies",
        "autoCreateConfigs": "Auto-Create Configurations",
        "recalculateLeaveQuotas": "Recalculate Leave Quotas",
        "recalculateOvertime": "Recalculate Overtime",
        "manageDepartmentRules": "Manage Department Rules",
        "reset": "Reset to Default",

        // Changes Page Features
        "view": "View",
        "promoteEmployee": "Promote Employee",
        "demoteEmployee": "Demote Employee",
        "transferEmployee": "Transfer Employee",
        "salaryAdjustment": "Salary Adjustment",
        "terminateEmployment": "Terminate Employment",
        "suspendEmployee": "Suspend Employee",
        "rehireEmployee": "Rehire Employee",
        "contractUpdate": "Contract Update",
        "jobRotation": "Job Rotation",
        "performanceReview": "Performance Review",
        "trainingAssignment": "Training Assignment",
        "approveActions": "Approve Actions",
        "rejectActions": "Reject Actions",
        "viewHistory": "View History",
        "viewPendingApprovals": "View Pending Approvals",
        "assignManagersToEmployees": "Assign Managers to Employees",
        "bulkActions": "Bulk Actions",
        "export": "Export",

        // Calendar Features
        "manageAttendees": "Manage Attendees",
        "exportCalendar": "Export Calendar",
        "shareCalendar": "Share Calendar",
        "manageTodos": "Manage To-Do List",
        "setReminders": "Set Reminders",
        "rsvpEvents": "RSVP to Events",
      },
      ar: {
        // Basic Access
        "viewConfigurations": "عرض الإعدادات",
        "editCompanyInfo": "تعديل معلومات الشركة",
        "add": "إنشاء",
        "remove": "حذف",

        // Attendance & Working Hours
        "editWorkSchedule": "تعديل جدول العمل",
        "editLateRules": "تعديل قواعد التأخير",
        "editAbsenceRules": "تعديل قواعد الغياب",
        "editGracePeriods": "تعديل فترات السماح",

        // Overtime Management
        "enableOvertime": "تفعيل/إيقاف العمل الإضافي",
        "editOvertimeRate": "تعديل معدل العمل الإضافي",
        "editOvertimeLimits": "تعديل حدود العمل الإضافي",
        "editOvertimeApproval": "تعديل موافقة العمل الإضافي",

        // Leave Policies
        "editAnnualLeaveProgression": "تعديل تدرج الإجازة السنوية",
        "editSickLeaveQuota": "تعديل حصة الإجازة المرضية",
        "leaveRules": "قواعد الإجازات",
        "editCasualLeaveQuota": "تعديل حصة الإجازة العارضة",
        "editLeaveApprovalRules": "تعديل قواعد موافقة الإجازة",
        "editLeaveCarryover": "تعديل ترحيل الإجازات",

        // Rewards & Bonuses
        "enablePerformanceBonus": "تفعيل مكافأة الأداء",
        "enableAttendanceBonus": "تفعيل مكافأة الحضور",
        "editBonusAmounts": "تعديل مبالغ المكافآت",
        "editBonusCriteria": "تعديل معايير المكافآت",

        // System Features
        "enablePrivateMessaging": "تفعيل الرسائل الخاصة",
        "enableDocumentSharing": "تفعيل مشاركة المستندات",
        "enablePerformanceReviews": "تفعيل تقييمات الأداء",
        "enableSalarySlips": "تفعيل قسائم الراتب",

        // Salary Advancements
        "enableAdvancements": "تفعيل السلف المالية",
        "editAdvancementLimits": "تعديل حدود السلف",
        "advancementRules": "قواعد السلفة",
        "editAdvancementPercentage": "تعديل نسبة السلف",
        "editAdvancementApproval": "تعديل موافقة السلف",
        "editRepaymentTerms": "تعديل شروط السداد",

        // Advanced Operations
        "detectCompanies": "اكتشاف الشركات",
        "autoCreateConfigs": "إنشاء تلقائي للإعدادات",
        "recalculateLeaveQuotas": "إعادة حساب حصص الإجازات",
        "recalculateOvertime": "إعادة حساب العمل الإضافي",
        "manageDepartmentRules": "إدارة قواعد الأقسام",
        "reset": "إعادة التعيين للافتراضي",

        // Changes Page Features
        "view": "عرض",
        "promoteEmployee": "ترقية موظف",
        "demoteEmployee": "تخفيض رتبة موظف",
        "transferEmployee": "نقل موظف",
        "salaryAdjustment": "تعديل الراتب",
        "terminateEmployment": "إنهاء العمل",
        "suspendEmployee": "إيقاف موظف",
        "rehireEmployee": "إعادة توظيف",
        "contractUpdate": "تحديث العقد",
        "jobRotation": "تدوير الوظائف",
        "performanceReview": "تقييم الأداء",
        "trainingAssignment": "تعيين التدريب",
        "approveActions": "الموافقة على الإجراءات",
        "rejectActions": "رفض الإجراءات",
        "viewHistory": "عرض السجل",
        "viewPendingApprovals": "عرض الموافقات المعلقة",
        "assignManagersToEmployees": "تعيين المديرين للموظفين",
        "bulkActions": "إجراءات جماعية",
        "export": "تصدير",

        // Calendar Features
        "manageAttendees": "إدارة الحضور",
        "exportCalendar": "تصدير التقويم",
        "shareCalendar": "مشاركة التقويم",
        "manageTodos": "إدارة قائمة المهام",
        "setReminders": "تعيين التذكيرات",
        "rsvpEvents": "الرد على الأحداث",
      }
    };

    return featureLabels[language]?.[featureName] || featureName;
  };

  const getCompaniesForClient = (clientName) => {
    const trimmedClient = String(clientName || "").trim();
    return Array.from(
      new Set(
        (companies || [])
          .filter((c) => String(c?.clientName || "").trim() === trimmedClient && c?.companyName)
          .map((c) => String(c.companyName).trim())
      )
    );
  };

  const safeUserLabel = (u) => {
    // Return English display name only (sr). Do not fall back to email or other fields.
    const en = (u?.employee?.sr && u.employee.sr.trim()) || (u?.sr && u.sr.trim());
    return en || "Unnamed User";
  };

  const encodePathSegment = (value) => encodeURIComponent(String(value ?? "").trim());
  const getTrimmedCompanyContext = (clientName, companyName) => ({
    client: String(clientName || "").trim(),
    company: String(companyName || "").trim(),
  });


  // Dot-style checkbox used in visibility modal
  const DotCheckbox = ({ checked, onChange }) => {
    const size = 18;
    const outerBorder = theme === "dark" ? "2px solid rgba(203,213,225,0.06)" : "2px solid rgba(100,116,139,0.4)";
    const bg = checked ? (theme === "dark" ? "#34d399" : "#059669") : "transparent";
    const innerSize = 8;

    const style = {
      width: size,
      height: size,
      minWidth: size,
      minHeight: size,
      borderRadius: "50%",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      boxSizing: "border-box",
      background: bg,
      border: checked ? "none" : outerBorder,
    };

    const inner = {
      width: innerSize,
      height: innerSize,
      borderRadius: "50%",
      background: "rgba(255,255,255,0.95)",
    };

    return (
      <div
        role="checkbox"
        aria-checked={checked}
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          onChange();
        }}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            onChange();
          }
        }}
        style={style}
      >
        {checked && <div style={inner} />}
      </div>
    );
  };

  // Available modules and features in the system
  // ===============================================
  // CORE MANAGEMENT MODULES
  // ===============================================
  const AVAILABLE_MODULES = [
    { name: "payroll", label: language === "ar" ? "الرواتب" : "Payroll" },
    { name: "attendance", label: language === "ar" ? "الحضور" : "Attendance" },
    { name: "leaves", label: language === "ar" ? "إدارة الإجازات" : "Leave Management" },
    { name: "documents", label: language === "ar" ? "المستندات" : "Documents" },
    { name: "reports", label: language === "ar" ? "التقارير" : "Reports" },
    { name: "advancements", label: "Solfa" },
    { name: "calendar", label: language === "ar" ? "التقويم" : "Calendar" },
    { name: "messages", label: language === "ar" ? "الرسائل" : "Messages" },
    { name: "configurations", label: language === "ar" ? "الإعدادات" : "Configurations" },
    { name: "adminLevels", label: language === "ar" ? "المستويات الإدارية" : "Admin Levels" },

    // ===============================================
    // EMPLOYEE MANAGEMENT
    // ===============================================
    { name: "employees", label: language === "ar" ? "إدارة الموظفين" : "Employee Management" },

    // ===============================================
    // EMPLOYEE LIFECYCLE CHANGES (SEPARATE MODULE)
    // ===============================================
    { name: "changesPage", label: language === "ar" ? "صفحة التغييرات" : "Changes Page" },
  ];

  const AVAILABLE_FEATURES = {
    // Core modules
    payroll: ["view", "process", "edit", "approve", "export", "import"],
    attendance: ["view", "edit", "import", "export"],
    leaves: ["view", "add", "approve", "reject", "remove"],
    documents: ["view", "upload", "download", "remove", "approve", "reject"],
    reports: ["view", "generate", "export", "schedule"],
    advancements: ["view", "add", "approve", "reject", "remove", "edit", "export"],
    calendar: ["view", "add", "edit", "remove", "manageAttendees", "exportCalendar", "shareCalendar", "manageTodos", "setReminders", "rsvpEvents"],
    messages: ["view", "send", "remove"],
    configurations: [
      "viewConfigurations",
      "editCompanyInfo",
      "editWorkSchedule",
      "editLateRules",
      "editAbsenceRules",
      "leaveRules",
      "editGracePeriods",
      "enableOvertime",
      "editOvertimeRate",
      "editOvertimeLimits",
      "editOvertimeApproval",
      "editAnnualLeaveProgression",
      "editSickLeaveQuota",
      "editCasualLeaveQuota",
      "editLeaveApprovalRules",
      "editLeaveCarryover",
      "enablePerformanceBonus",
      "enableAttendanceBonus",
      "editBonusAmounts",
      "editBonusCriteria",
      "enablePrivateMessaging",
      "enableDocumentSharing",
      "enablePerformanceReviews",
      "enableSalarySlips",
      "enableAdvancements",
      "editAdvancementLimits",
      "editAdvancementPercentage",
      "editAdvancementApproval",
      "editRepaymentTerms",
      "detectCompanies",
      "advancementRules",
      "autoCreateConfigs",
      "recalculateLeaveQuotas",
      "recalculateOvertime",
      "manageDepartmentRules",
      "reset"
    ],
    adminLevels: ["view", "add", "edit", "remove"],

    // ===============================================
    // EMPLOYEE MANAGEMENT
    // ===============================================
    employees: ["view", "add", "edit", "remove", "export", "import"],

    // ===============================================
    // EMPLOYEE LIFECYCLE CHANGES (SEPARATE MODULE)
    // Features for managing employee lifecycle events
    // ===============================================
    changesPage: [
      "view",
      "promoteEmployee",
      "demoteEmployee",
      "transferEmployee",
      "salaryAdjustment",
      "terminateEmployment",
      "suspendEmployee",
      "rehireEmployee",
      "contractUpdate",
      "jobRotation",
      "performanceReview",
      "trainingAssignment",
      "approveActions",
      "rejectActions",
      "viewHistory",
      "viewPendingApprovals",
      "assignManagersToEmployees",
      "bulkActions",
      "export"
    ],
  };

  // Auto-clear success message after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Auto-clear error message after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Set clientName from /users/me (fallback to localStorage)
  useEffect(() => {
    const clientFromProfile = profile?.clientName;
    const clientFromStorage = localStorage.getItem("clientName") || localStorage.getItem("client") || "";
    const resolvedClient = clientFromProfile || clientFromStorage;
    console.log(
      "[AdminLevelConfiguration] clientName source:",
      clientFromProfile ? `✅ /users/me (${clientFromProfile})` : `⚠️ localStorage (${clientFromStorage})`
    );
    if (resolvedClient) {
      setSelectedCompany((prev) => ({ ...prev, client: String(resolvedClient).trim() }));
    }
  }, [profile]);

  // Fetch companies from company-config endpoint (filter by client if available)
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setCompaniesLoading(true);
        // ✅ Use apiFetch instead of axios
        const response = await apiFetch(`${API_BASE_URL}/company-config/all`, {
          method: 'GET'
        });
        const data = await response.json();

        const companiesList = Array.isArray(data)
          ? data
          : (data.data || data.companies || data.companyConfigs || []);

        if (selectedCompany.client) {
          const trimmedClient = String(selectedCompany.client).trim();
          const filtered = companiesList.filter((c) => String(c?.clientName || "").trim() === trimmedClient);
          setCompanies(filtered);
        } else {
          setCompanies(companiesList);
        }
      } catch (err) {
        console.error("Error fetching companies:", err);
        setError("Failed to load companies");
        setCompanies([]);
      } finally {
        setCompaniesLoading(false);
      }
    };

    fetchCompanies();
    // Re-run when client changes so we only show companies for that client
  }, [API_BASE_URL, selectedCompany.client]);

  // Fetch admin levels for selected company
  const fetchAdminLevels = async (clientName, workingCompany) => {
    const trimmedClient = String(clientName || "").trim();
    const trimmedCompany = String(workingCompany || "").trim();
    if (!trimmedClient || !trimmedCompany) return;

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      // ✅ Use apiFetch instead of axios
      const response = await apiFetch(
        `${API_BASE_URL}/admin-levels/company/${encodePathSegment(trimmedClient)}/${encodePathSegment(trimmedCompany)}`,
        { method: 'GET' }
      );
      const data = await response.json();
      setAdminLevels(data.adminLevels || []);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching admin levels:", err);
    } finally {
      setLoading(false);
    }
  };

  // Refresh current user's admin level data if they are affected by permission changes
  const refreshUserAdminLevel = async () => {
    try {
      const userId = profile?.userId || localStorage.getItem('userId');
      if (!userId) return;

      // ✅ Use apiFetch instead of axios
      const response = await apiFetch(`${API_BASE_URL}/users/${userId}/admin-level`, {
        method: 'GET'
      });
      const data = await response.json();

      if (data) {
        // Update localStorage with new admin level data
        localStorage.setItem('adminLevel', JSON.stringify(data));

        // Trigger a storage event to notify other components
        window.dispatchEvent(new Event('storage'));

        console.log('✅ User admin level refreshed:', response.data);
      }
    } catch (err) {
      console.log('ℹ️ Could not refresh user admin level:', err.message);
    }
  };

  // Create new admin level
  const handleCreateAdminLevel = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { client: trimmedClient, company: trimmedCompany } = getTrimmedCompanyContext(
        selectedCompany.client,
        selectedCompany.company
      );
      // ✅ Use apiFetch instead of axios
      const res = await apiFetch(`${API_BASE_URL}/admin-levels`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',  // ADD THIS
        },
        body: JSON.stringify({
          ...formData,
          clientName: trimmedClient,
          workingCompany: trimmedCompany,
        })
      });
      await res.json();

      setSuccess(t.created);
      setShowCreateModal(false);
      setFormData({ name: "", description: "", clientName: "", workingCompany: "" });

      fetchAdminLevels(trimmedClient, trimmedCompany);
    } catch (err) {
      setError(err.message);
      console.error("Error creating admin level:", err);
    } finally {
      setLoading(false);
    }
  };

  // Update module permission
  const handleSetModulePermission = async (levelId, module, isEnabled) => {
    setError(null);
    try {
      const { client: trimmedClient, company: trimmedCompany } = getTrimmedCompanyContext(
        selectedCompany.client,
        selectedCompany.company
      );
      // ✅ Use apiFetch instead of axios
      const res = await apiFetch(`${API_BASE_URL}/admin-levels/${levelId}/modules`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',  // ADD THIS
        },

        body: JSON.stringify({
          module,
          workingCompany: trimmedCompany,
          isEnabled,
        })
      });
      await res.json();

      setSuccess(t.moduleUpdated);
      fetchAdminLevels(trimmedClient, trimmedCompany);

      // Refresh current user's admin level if they might be affected
      await refreshUserAdminLevel();
    } catch (err) {
      setError(err.message);
      console.error("Error updating module:", err);
    }
  };

  // Map UI feature keys to safe backend keys
  const mapFeatureKey = (key) => {
    if (!key) return key;
    if (key === 'create') return 'add';
    if (key === 'delete') return 'remove';
    return key;
  };

  // Update feature permission
  const handleSetFeaturePermission = async (modulePermissionId, featureName, isEnabled) => {
    setError(null);
    try {
      // ✅ Use apiFetch instead of axios
      const safeFeatureName = mapFeatureKey(featureName);
      const res = await apiFetch(
        `${API_BASE_URL}/admin-levels/modules/${modulePermissionId}/features`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',  // ADD THIS
          },
          body: JSON.stringify({
            featureName: safeFeatureName,
            isEnabled,
          })
        }
      );
      await res.json();

      setSuccess(t.featureUpdated);
      const { client: trimmedClient, company: trimmedCompany } = getTrimmedCompanyContext(
        selectedCompany.client,
        selectedCompany.company
      );
      fetchAdminLevels(trimmedClient, trimmedCompany);
    } catch (err) {
      setError(err.message);
      console.error("Error updating feature:", err);
    }
  };

  // Delete admin level
  const handleDeleteAdminLevel = async (levelId) => {
    setDeletingLevelId(levelId);
    setShowDeleteConfirm(true);
  };

  // Confirm delete
  const confirmDeleteAdminLevel = async () => {
    if (!deletingLevelId) return;

    setError(null);
    setShowDeleteConfirm(false);
    try {
      // ✅ Use apiFetch instead of axios
      const res = await apiFetch(`${API_BASE_URL}/admin-levels/${deletingLevelId}`, {
        method: 'DELETE'
      });
      await res.json();

      setSuccess(t.deleted);
      setDeletingLevelId(null);
      const { client: trimmedClient, company: trimmedCompany } = getTrimmedCompanyContext(
        selectedCompany.client,
        selectedCompany.company
      );
      fetchAdminLevels(trimmedClient, trimmedCompany);
    } catch (err) {
      setError(err.message);
      console.error("Error deleting admin level:", err);
      setDeletingLevelId(null);
    }
  };

  // Add module to admin level
  const handleAddModule = async () => {
    if (!selectedLevelForModule || !selectedModuleName) {
      setError(t.selectModuleError);
      return;
    }

    setError(null);
    try {
      const { client: trimmedClient, company: trimmedCompany } = getTrimmedCompanyContext(
        selectedCompany.client,
        selectedCompany.company
      );
      // ✅ Use apiFetch instead of axios
      const res = await apiFetch(
        `${API_BASE_URL}/admin-levels/${selectedLevelForModule}/modules`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',  // ADD THIS
          },
          body: JSON.stringify({
            module: selectedModuleName,
            workingCompany: trimmedCompany,
            isEnabled: true,
          })
        }
      );
      await res.json();

      setSuccess(t.moduleAdded);
      setShowModuleModal(false);
      setSelectedModuleName("");
      fetchAdminLevels(trimmedClient, trimmedCompany);

      // Refresh current user's admin level if they might be affected
      await refreshUserAdminLevel();
    } catch (err) {
      setError(err.message);
      console.error("Error adding module:", err);
    }
  };

  // Add feature to module
  const handleAddFeature = async () => {
    if (!selectedModuleForFeature || !selectedFeatureName) {
      setError(t.selectFeatureError);
      return;
    }

    setError(null);
    try {
      // ✅ Use apiFetch instead of axios
      const res = await apiFetch(
        `${API_BASE_URL}/admin-levels/modules/${selectedModuleForFeature}/features`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',  // ADD THIS
          },
          body: JSON.stringify({
            featureName: mapFeatureKey(selectedFeatureName),
            isEnabled: true,
          })
        }
      );
      await res.json();

      setSuccess(t.featureAdded);
      setShowFeatureModal(false);
      setSelectedFeatureName("");
      const { client: trimmedClient, company: trimmedCompany } = getTrimmedCompanyContext(
        selectedCompany.client,
        selectedCompany.company
      );
      fetchAdminLevels(trimmedClient, trimmedCompany);
    } catch (err) {
      setError(err.message);
      console.error("Error adding feature:", err);
    }
  };

  const fetchDepartmentsForCompany = async (clientName, companyName) => {
    const { client, company } = getTrimmedCompanyContext(clientName, companyName);
    if (!client || !company) return;
    try {
      console.log(`[AdminLevel] Fetching departments - clientName: ${client}, workingCompany: ${company}`);
      // ✅ Use apiFetch instead of axios
      const res = await apiFetch(
        `${API_BASE_URL}/admin-levels/visibility/options/departments/${encodePathSegment(client)}/${encodePathSegment(company)}`,
        { method: 'GET' }
      );
      const data = await res.json();
      const list = data?.departments || [];
      console.log(`[AdminLevel] Fetched ${list.length} departments`);
      setDepartmentsByCompany((prev) => ({ ...prev, [company]: list }));
      return list;
    } catch (err) {
      console.error("Error fetching departments:", err);
      setDepartmentsByCompany((prev) => ({ ...prev, [company]: [] }));
      setError(err.message || "Failed to load departments");
      return [];
    }
  };

  const fetchUsersForCompany = async (clientName, companyName, employeeTypes) => {
    const { client, company } = getTrimmedCompanyContext(clientName, companyName);
    if (!client || !company) return;
    try {
      const types = Array.isArray(employeeTypes) ? employeeTypes : [];
      const query = new URLSearchParams({
        clientName: client,
        workingCompany: company,
        ...(types.length ? { employeeTypes: types.join(",") } : {}),
      }).toString();
      console.log(`[AdminLevel] Fetching users for departments:`, types);
      // ✅ Use apiFetch instead of axios
      const res = await apiFetch(`${API_BASE_URL}/admin-levels/visibility/options/users?${query}`, {
        method: 'GET'
      });
      const data = await res.json();
      const list = data?.users || [];
      console.log(`[AdminLevel] Fetched ${list.length} users`);
      setUsersByCompany((prev) => ({ ...prev, [company]: list }));

      // Sync user selection with the departments passed to this function
      // Auto-select users from departments in 'types' array, keep manually selected users from other departments
      setSelectedUsersByCompany((prevUsers) => {
        const currentUserIds = prevUsers[company] || [];

        // Get all users that should be auto-selected (from the passed departments)
        const usersFromSelectedDepts = list
          .filter(u => types.includes(u?.employee?.employeeType || ""))
          .map(u => u.id);

        const manuallySelectedUsers = currentUserIds.filter(id => {
          const user = list.find(u => u.id === id);
          // Keep users that are either: not in the fetched list, or not from any of the selected departments
          return !user || !types.includes(user?.employee?.employeeType || "");
        });

        // Combine auto-selected users from selected departments + manually selected users
        const finalUserIds = [...new Set([...usersFromSelectedDepts, ...manuallySelectedUsers])];

        console.log(`[AdminLevel] Auto-selected ${usersFromSelectedDepts.length} users from departments [${types.join(', ')}], kept ${manuallySelectedUsers.length} manually selected`);

        return { ...prevUsers, [company]: finalUserIds };
      });

      return list;
    } catch (err) {
      console.error("Error fetching users:", err);
      setUsersByCompany((prev) => ({ ...prev, [company]: [] }));
      setError(err.message || "Failed to load users");
      return [];
    }
  };

  const openVisibilityControl = async (level) => {
    if (!level?.id) return;
    setError(null);
    setSuccess(null);
    setVisibilitySearch("");
    setSelectedLevelForVisibility(level);
    setShowVisibilityModal(true);
    setVisibilityRulesLoading(true);

    try {
      // ✅ Use apiFetch instead of axios
      const res = await apiFetch(`${API_BASE_URL}/admin-levels/${level.id}/visibility`, {
        method: 'GET'
      });
      const data = await res.json();
      const rules = data?.rules || [];

      const companySet = new Set();
      const deptMap = {};
      const userMap = {};

      rules.forEach((r) => {
        const companyName = String(r.targetWorkingCompany || "").trim();
        if (!companyName) return;

        companySet.add(companyName);

        if (r.scopeType === "DEPARTMENT" && r.targetEmployeeType) {
          deptMap[companyName] = Array.from(new Set([...(deptMap[companyName] || []), r.targetEmployeeType]));
        }

        if (r.scopeType === "USER" && r.targetInfinityCode) {
          userMap[companyName] = Array.from(new Set([...(userMap[companyName] || []), r.targetInfinityCode]));
        }
      });

      const selectedCompanies = Array.from(companySet);
      setSelectedVisibilityCompanies(selectedCompanies);
      setSelectedDepartmentsByCompany(deptMap);

      const userIdsByCompany = {};
      await Promise.all(
        selectedCompanies.map(async (rawCompanyName) => {
          const companyName = String(rawCompanyName || "").trim();
          if (!companyName) {
            return;
          }
          await fetchDepartmentsForCompany(selectedCompany.client, companyName);
          const deptTypes = deptMap[companyName] || [];
          const list = await fetchUsersForCompany(selectedCompany.client, companyName, deptTypes);
          const codes = userMap[companyName] || [];
          userIdsByCompany[companyName] = (list || [])
            .filter((u) => codes.includes(u?.infinityCode))
            .map((u) => u.id);
        })
      );
      setSelectedUsersByCompany(userIdsByCompany);
    } catch (err) {
      console.error("Error loading visibility rules:", err);
      setError(err.message || "Failed to load visibility rules");
    } finally {
      setVisibilityRulesLoading(false);
    }
  };

  const handleToggleCompany = async (companyName) => {
    const normalizedCompany = String(companyName || "").trim();
    if (!normalizedCompany) return;

    const normalizedSelection = selectedVisibilityCompanies
      .map((c) => String(c || "").trim())
      .filter(Boolean);

    const isSelected = normalizedSelection.includes(normalizedCompany);
    const next = isSelected
      ? normalizedSelection.filter((c) => c !== normalizedCompany)
      : [...new Set([...normalizedSelection, normalizedCompany])];

    setSelectedVisibilityCompanies(next);

    if (!next.includes(normalizedCompany)) {
      setSelectedDepartmentsByCompany((prev) => {
        const copy = { ...prev };
        delete copy[normalizedCompany];
        return copy;
      });
      setSelectedUsersByCompany((prev) => {
        const copy = { ...prev };
        delete copy[normalizedCompany];
        return copy;
      });
      return;
    }

    await fetchDepartmentsForCompany(selectedCompany.client, normalizedCompany);
  };

  // Select / Deselect All companies for the current client
  const toggleSelectAllCompanies = async () => {
    const all = getCompaniesForClient(selectedCompany.client) || [];
    const allNames = all.slice();

    if (allNames.length === 0) return;

    const normalizedSelection = selectedVisibilityCompanies
      .map((c) => String(c || "").trim())
      .filter(Boolean);
    const isAllSelected = allNames.every((c) => normalizedSelection.includes(String(c || "").trim()));

    if (isAllSelected) {
      setSelectedVisibilityCompanies([]);
      setSelectedDepartmentsByCompany({});
      setSelectedUsersByCompany({});
      return;
    }

    setSelectedVisibilityCompanies(allNames);

    // load departments and users for each company
    await Promise.all(
      allNames.map(async (companyName) => {
        await fetchDepartmentsForCompany(selectedCompany.client, companyName);
        await fetchUsersForCompany(selectedCompany.client, companyName, []);
      })
    );
  };

  // Toggle select-all for a company's departments
  const toggleSelectAllDepartments = async (companyName) => {
    const normalizedCompany = String(companyName || "").trim();
    if (!normalizedCompany) return;
    const list = departmentsByCompany[normalizedCompany] || [];
    if (!list || list.length === 0) return;

    const current = selectedDepartmentsByCompany[normalizedCompany] || [];
    const isAll = list.length === current.length && list.every((d) => current.includes(d));

    const next = isAll ? [] : list.slice();
    setSelectedDepartmentsByCompany((prev) => ({ ...prev, [normalizedCompany]: next }));

    // when departments set, load users for those departments
    await fetchUsersForCompany(selectedCompany.client, normalizedCompany, next);
  };

  // Toggle select-all for visible users in a company
  // eslint-disable-next-line no-unused-vars
  const toggleSelectAllUsers = (companyName) => {
    const normalizedCompany = String(companyName || "").trim();
    if (!normalizedCompany) return;
    const list = usersByCompany[normalizedCompany] || [];
    const filtered = list.filter((u) => {
      if (!visibilitySearch) return true;
      const v = visibilitySearch.toLowerCase();
      return safeUserLabel(u).toLowerCase().includes(v);
    });

    const ids = filtered.slice(0, 200).map((u) => u.id);
    const current = selectedUsersByCompany[normalizedCompany] || [];
    const isAll = ids.length > 0 && ids.every((id) => current.includes(id));

    const next = isAll ? [] : ids;
    setSelectedUsersByCompany((prev) => ({ ...prev, [normalizedCompany]: next }));

    // when selecting specific users, clear departments to indicate explicit selection
    if (next.length > 0) {
      setSelectedDepartmentsByCompany((prev) => ({ ...prev, [normalizedCompany]: [] }));
    }
  };

  // Toggle select-all for users of a specific department in a company
  const toggleSelectAllDepartmentUsers = (companyName, dept) => {
    const normalizedCompany = String(companyName || "").trim();
    if (!normalizedCompany) return;
    const list = usersByCompany[normalizedCompany] || [];
    const deptUsers = list.filter((u) => (u?.employee?.employeeType || "-") === dept).map((u) => u.id);
    if (deptUsers.length === 0) return;

    const current = selectedUsersByCompany[normalizedCompany] || [];
    const isAll = deptUsers.every((id) => current.includes(id));

    let next;
    if (isAll) {
      next = current.filter((id) => !deptUsers.includes(id));
    } else {
      next = Array.from(new Set([...current, ...deptUsers]));
    }

    setSelectedUsersByCompany((prev) => ({ ...prev, [normalizedCompany]: next }));

    // if selecting explicit users, clear department-level selection state
    if (next.length > 0) {
      setSelectedDepartmentsByCompany((prev) => ({ ...prev, [normalizedCompany]: [] }));
    }
  };

  const handleToggleDepartment = async (companyName, dept) => {
    const normalizedCompany = String(companyName || "").trim();
    if (!normalizedCompany) return;
    const current = selectedDepartmentsByCompany[normalizedCompany] || [];
    const isRemoving = current.includes(dept);
    const next = isRemoving ? current.filter((d) => d !== dept) : [...current, dept];

    console.log(`[AdminLevel] Toggling department: ${dept}, isRemoving: ${isRemoving}, next departments:`, next);

    setSelectedDepartmentsByCompany((prev) => ({ ...prev, [normalizedCompany]: next }));

    // Fetch users and let fetchUsersForCompany handle auto-selection based on 'next' departments
    await fetchUsersForCompany(selectedCompany.client, normalizedCompany, next);
  };

  const handleToggleUser = (companyName, userId) => {
    const normalizedCompany = String(companyName || "").trim();
    if (!normalizedCompany) return;
    const current = selectedUsersByCompany[normalizedCompany] || [];
    const next = current.includes(userId) ? current.filter((x) => x !== userId) : [...current, userId];
    setSelectedUsersByCompany((prev) => ({ ...prev, [normalizedCompany]: next }));
  };

  const saveVisibilityRules = async () => {
    if (!selectedLevelForVisibility?.id) return;
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const rules = [];

      selectedVisibilityCompanies.forEach((rawCompanyName) => {
        const companyName = String(rawCompanyName || "").trim();
        if (!companyName) {
          return;
        }
        const selectedDepts = selectedDepartmentsByCompany[companyName] || [];
        const selectedUserIds = selectedUsersByCompany[companyName] || [];
        const allUsers = usersByCompany[companyName] || [];
        const allDepartments = departmentsByCompany[companyName] || [];

        // Check if ALL departments are selected AND all users in those departments are selected
        const allDeptsSelected = allDepartments.length > 0 &&
          allDepartments.every(dept => selectedDepts.includes(dept));

        const allUsersInSelectedDeptsSelected = allUsers.length > 0 &&
          allUsers.every(u => selectedUserIds.includes(u.id));

        // If all departments and all their users are selected, use COMPANY scope
        if (allDeptsSelected && allUsersInSelectedDeptsSelected) {
          console.log(`[AdminLevel] Using COMPANY scope for ${companyName} (all depts and users selected)`);
          rules.push({
            scopeType: "COMPANY",
            targetWorkingCompany: companyName,
          });
          return; // Skip processing individual departments/users for this company
        }

        // If no departments and no users are selected, also send COMPANY scope
        if (selectedDepts.length === 0 && selectedUserIds.length === 0) {
          console.log(`[AdminLevel] Using COMPANY scope for ${companyName} (no specific selections)`);
          rules.push({
            scopeType: "COMPANY",
            targetWorkingCompany: companyName,
          });
          return;
        }

        // Track which users are handled by department scope
        const usersHandledByDeptScope = new Set();

        // For each selected department, check if ALL users in that department are selected
        selectedDepts.forEach((dept) => {
          const deptUsers = allUsers.filter(u => (u?.employee?.employeeType || "") === dept);
          const deptUserIds = deptUsers.map(u => u.id);
          const allDeptUsersSelected = deptUserIds.length > 0 && deptUserIds.every(id => selectedUserIds.includes(id));

          if (allDeptUsersSelected) {
            // All users in department are selected -> send as DEPARTMENT scope
            console.log(`[AdminLevel] Using DEPARTMENT scope for ${companyName} - ${dept}`);
            rules.push({
              scopeType: "DEPARTMENT",
              targetWorkingCompany: companyName,
              targetEmployeeType: dept,
            });
            // Mark these users as handled
            deptUserIds.forEach(id => usersHandledByDeptScope.add(id));
          } else {
            // Some users in this department are deselected
            // Send only the selected users from this department as USER scope
            const partiallySelectedUserIds = deptUserIds.filter(id => selectedUserIds.includes(id));
            console.log(`[AdminLevel] Using USER scope for ${companyName} - ${dept} (${partiallySelectedUserIds.length} users)`);
            partiallySelectedUserIds.forEach((uid) => {
              const u = allUsers.find((x) => x.id === uid);
              if (u?.infinityCode) {
                rules.push({
                  scopeType: "USER",
                  targetWorkingCompany: companyName,
                  targetInfinityCode: u.infinityCode,
                  targetEmployeeType: u?.employee?.employeeType || null,
                });
                usersHandledByDeptScope.add(uid);
              }
            });
          }
        });

        // Handle users that are selected but their department is NOT in selectedDepts
        // (users selected individually without their department being checked)
        selectedUserIds.forEach((uid) => {
          if (!usersHandledByDeptScope.has(uid)) {
            const u = allUsers.find((x) => x.id === uid);
            if (u?.infinityCode) {
              console.log(`[AdminLevel] Using USER scope for individual user in ${companyName}`);
              rules.push({
                scopeType: "USER",
                targetWorkingCompany: companyName,
                targetInfinityCode: u.infinityCode,
                targetEmployeeType: u?.employee?.employeeType || null,
              });
            }
          }
        });
      });

      // ✅ Use apiFetch instead of axios
      const res = await apiFetch(`${API_BASE_URL}/admin-levels/${selectedLevelForVisibility.id}/visibility`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',  // ADD THIS
        },
        body: JSON.stringify({ rules })
      });
      await res.json();

      setSuccess(t.visibilitySaved);
      setShowVisibilityModal(false);
      setSelectedLevelForVisibility(null);
      const { client: trimmedClient, company: trimmedCompany } = getTrimmedCompanyContext(
        selectedCompany.client,
        selectedCompany.company
      );
      fetchAdminLevels(trimmedClient, trimmedCompany);

      // Refresh current user's admin level if they might be affected
      await refreshUserAdminLevel();
    } catch (err) {
      console.error("Error saving visibility rules:", err);
      setError(err.message || "Failed to save visibility rules");
    } finally {
      setLoading(false);
    }
  };

  const openAssignEmployees = async (level) => {
    if (!level?.id) return;
    if (!selectedCompany.client || !selectedCompany.company) {
      setError("Please select a client and working company first");
      return;
    }

    setError(null);
    setSuccess(null);
    setAssignSearch("");
    setSelectedLevelForAssign(level);
    setShowAssignModal(true);
    setAssignLoading(true);

    try {
      const { client: trimmedClient, company: trimmedCompany } = getTrimmedCompanyContext(
        selectedCompany.client,
        selectedCompany.company
      );
      console.log(`[AdminLevel] Opening assign employees - clientName: ${trimmedClient}, workingCompany: ${trimmedCompany}`);
      const query = new URLSearchParams({
        clientName: trimmedClient,
        workingCompany: trimmedCompany,
      }).toString();
      // ✅ Use apiFetch instead of axios
      const res = await apiFetch(`${API_BASE_URL}/admin-levels/visibility/options/users?${query}`, {
        method: 'GET'
      });
      const data = await res.json();
      const list = data?.users || [];
      console.log(`[AdminLevel] Fetched ${list.length} users for assignment`);
      setAssignCandidates(list);

      const already = Array.isArray(level?.users) ? level.users.map((u) => u.id) : [];
      setAssignSelectedUserIds(already);
    } catch (err) {
      console.error("Error loading assign candidates:", err);
      setError(err.message || "Failed to load users");
      setAssignCandidates([]);
      setAssignSelectedUserIds([]);
    } finally {
      setAssignLoading(false);
    }
  };

  const toggleAssignUser = (userId) => {
    setAssignSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const saveAssignments = async () => {
    if (!selectedLevelForAssign?.id) return;
    setError(null);
    setSuccess(null);
    setAssignLoading(true);
    try {
      // ✅ Use apiFetch instead of axios
      const res = await apiFetch(`${API_BASE_URL}/admin-levels/${selectedLevelForAssign.id}/assign-bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',  // ADD THIS
        },
        body: JSON.stringify({
          userIds: assignSelectedUserIds,
        })
      });
      await res.json();
      setSuccess(t.assignmentsSaved);
      setShowAssignModal(false);
      setSelectedLevelForAssign(null);
      const { client: trimmedClient, company: trimmedCompany } = getTrimmedCompanyContext(
        selectedCompany.client,
        selectedCompany.company
      );
      fetchAdminLevels(trimmedClient, trimmedCompany);

      // Refresh current user's admin level since they might have been assigned
      await refreshUserAdminLevel();
    } catch (err) {
      console.error("Error saving assignments:", err);
      setError(err.message || "Failed to assign users");
    } finally {
      setAssignLoading(false);
    }
  };

  // Styles - Modern Design System (Matching Dashboard/EmployeeTable)
  const styles = {
    pageContainer: {
      minHeight: "100vh",
      background: theme === "dark"
        ? "linear-gradient(135deg, #0f1419 0%, #1a1f35 50%, #0d1628 100%)"
        : "linear-gradient(135deg, #f8fafb 0%, #eef2f5 50%, #e5f0f7 100%)",
      padding: "24px",
      fontFamily: language === "ar" ? "'Cairo', 'Segoe UI', sans-serif" : "'Segoe UI', 'Helvetica Neue', sans-serif",
      direction: language === "ar" ? "rtl" : "ltr",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "32px",
      flexWrap: "wrap",
      gap: "16px",
    },
    titleSection: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
    },
    iconWrapper: {
      width: "56px",
      height: "56px",
      borderRadius: "16px",
      background: theme === "dark"
        ? "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
        : "linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    title: {
      fontSize: "28px",
      fontWeight: "700",
      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
      margin: 0,
      letterSpacing: "-0.5px",
    },
    headerActions: {
      display: "flex",
      gap: "12px",
      alignItems: "center",
    },
    languageToggle: {
      padding: "10px 20px",
      borderRadius: "10px",
      border: "none",
      background: theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
      color: theme === "dark" ? "#e2e8f0" : "#334155",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "600",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },
    card: {
      background: theme === "dark"
        ? "rgba(15, 23, 42, 0.8)"
        : "rgba(255, 255, 255, 0.95)",
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      borderRadius: "20px",
      padding: "28px",
      marginBottom: "24px",
      border: theme === "dark"
        ? "1px solid rgba(148, 163, 184, 0.15)"
        : "1px solid rgba(0, 0, 0, 0.05)",
      boxShadow: theme === "dark"
        ? "0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
        : "0 8px 32px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
      transition: "all 0.3s ease",
    },
    cardTitle: {
      fontSize: "18px",
      fontWeight: "700",
      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
      marginBottom: "20px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      letterSpacing: "-0.3px",
    },
    button: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      padding: "12px 24px",
      borderRadius: "12px",
      border: "none",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "600",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      fontFamily: "inherit",
    },
    primaryButton: {
      background: "#2e7cba",
      color: "#ffffff",
    },
    secondaryButton: {
      background: theme === "dark" ? "rgba(148, 163, 184, 0.12)" : "rgba(15, 23, 42, 0.06)",
      color: theme === "dark" ? "#cbd5e1" : "#374151",
      border: theme === "dark" ? "1px solid rgba(148, 163, 184, 0.2)" : "1px solid rgba(15, 23, 42, 0.1)",
    },
    dangerButton: {
      background: theme === "dark"
        ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
        : "linear-gradient(135deg, #f87171 0%, #ef4444 100%)",
      color: "#ffffff",
    },
    inputGroup: {
      marginBottom: "20px",
    },
    label: {
      display: "block",
      fontSize: "13px",
      fontWeight: "600",
      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
      marginBottom: "8px",
    },
    input: {
      width: "100%",
      padding: "12px 14px",
      borderRadius: "12px",
      // border: theme === "dark"
      // ? "1px solid rgba(148, 163, 184, 0.2)"
      //: "1px solid rgba(15, 23, 42, 0.1)",
      background: theme === 'dark' ? 'rgba(51, 65, 85, 1.8)' : '#ffffff',
      border: theme === 'dark' ? '1px solid rgba(148, 163, 184, 0.3)' : '1px solid rgba(148, 163, 184, 0.3)',     // color: theme === "dark" ? "#f1f5f9" : "#334155",
      fontSize: "14px",
      fontFamily: "inherit",
      transition: "all 0.3s ease",
      boxSizing: "border-box",
    },
    textarea: {
      minHeight: "100px",
      resize: "vertical",
    },
    levelCard: {
      background: theme === "dark"
        ? "rgba(30, 41, 59, 0.6)"
        : "rgba(248, 250, 252, 0.7)",
      borderRadius: "16px",
      padding: "20px",
      marginBottom: "16px",
      border: theme === "dark"
        ? "1px solid rgba(148, 163, 184, 0.15)"
        : "1px solid rgba(15, 23, 42, 0.05)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      cursor: "pointer",
      boxShadow: theme === "dark"
        ? "0 2px 8px rgba(0, 0, 0, 0.2)"
        : "0 2px 8px rgba(0, 0, 0, 0.04)",
    },
    levelHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    levelInfo: {
      flex: 1,
    },
    levelName: {
      fontSize: "16px",
      fontWeight: "700",
      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
      marginBottom: "6px",
    },
    levelDesc: {
      fontSize: "13px",
      color: theme === "dark" ? "#94a3b8" : "#64748b",
      margin: 0,
    },
    levelActions: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    iconButton: {
      background: "transparent",
      border: "none",
      cursor: "pointer",
      padding: "8px",
      borderRadius: "8px",
      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
    },
    expandedContent: {
      marginTop: "20px",
      paddingTop: "20px",
      borderTop: theme === "dark"
        ? "1px solid rgba(148, 163, 184, 0.15)"
        : "1px solid rgba(203, 213, 225, 0.5)",
    },
    actionButtons: {
      display: "flex",
      gap: "10px",
      flexWrap: "wrap",
      marginBottom: "20px",
    },
    smallButton: {
      padding: "8px 16px",
      fontSize: "13px",
      borderRadius: "10px",
    },
    moduleCard: {
      background: theme === "dark"
        ? "rgba(15, 23, 42, 0.5)"
        : "rgba(255, 255, 255, 0.8)",
      padding: "18px",
      borderRadius: "12px",
      marginBottom: "12px",
      border: theme === "dark"
        ? "1px solid rgba(148, 163, 184, 0.15)"
        : "1px solid rgba(15, 23, 42, 0.05)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      position: "relative",
    },
    moduleHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "12px",
    },
    moduleName: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    toggle: {
      width: "48px",
      height: "26px",
      borderRadius: "13px",
      border: "none",
      cursor: "pointer",
      position: "relative",
      transition: "all 0.3s ease",
      background: theme === "dark" ? "rgba(100, 120, 150, 0.3)" : "rgba(200, 210, 230, 0.4)",
    },
    toggleActive: {
      background: theme === "dark"
        ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
        : "linear-gradient(135deg, #34d399 0%, #10b981 100%)",
      boxShadow: theme === "dark"
        ? "0 4px 12px rgba(16, 185, 129, 0.35)"
        : "0 4px 12px rgba(16, 185, 129, 0.25)",
    },
    toggleButton: {
      position: "absolute",
      width: "22px",
      height: "22px",
      borderRadius: "50%",
      background: "#ffffff",
      top: "2px",
      transition: "all 0.3s ease",
      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    },
    featuresList: {
      paddingLeft: language === "ar" ? "0" : "20px",
      paddingRight: language === "ar" ? "20px" : "0",
    },
    featureItem: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "6px 0",
      fontSize: "13px",
      color: theme === "dark" ? "#cbd5e1" : "#475569",
    },
    smallToggle: {
      width: "36px",
      height: "20px",
      borderRadius: "10px",
    },
    smallToggleButton: {
      width: "16px",
      height: "16px",
    },
    modal: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0, 0, 0, 0.65)",
      backdropFilter: "blur(12px)",
      WebkitBackdropFilter: "blur(12px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
      padding: "20px",
      animation: "fadeIn 0.2s ease-out",
    },
    modalContent: {
      background: theme === "dark"
        ? "rgba(15, 23, 42, 0.95)"
        : "rgba(255, 255, 255, 0.98)",
      borderRadius: "24px",
      padding: "32px",
      maxWidth: "600px",
      width: "100%",
      maxHeight: "90vh",
      overflowY: "auto",
      border: theme === "dark"
        ? "1px solid rgba(148, 163, 184, 0.15)"
        : "1px solid rgba(0, 0, 0, 0.05)",
      boxShadow: theme === "dark"
        ? "0 20px 60px rgba(0, 0, 0, 0.4)"
        : "0 20px 60px rgba(0, 0, 0, 0.15)",
    },
    modalHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
    },
    modalTitle: {
      fontSize: "22px",
      fontWeight: "700",
      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
      margin: 0,
    },
    modalBody: {
      marginBottom: "24px",
    },
    modalFooter: {
      display: "flex",
      gap: "12px",
      justifyContent: "flex-end",
    },
    alert: {
      padding: "16px 20px",
      borderRadius: "12px",
      marginBottom: "20px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      fontSize: "14px",
      fontWeight: "500",
      animation: "slideIn 0.3s ease",
    },
    alertSuccess: {
      background: theme === "dark"
        ? "rgba(16, 185, 129, 0.15)"
        : "rgba(16, 185, 129, 0.1)",
      border: `1px solid ${theme === "dark" ? "rgba(16, 185, 129, 0.3)" : "rgba(16, 185, 129, 0.2)"}`,
      color: theme === "dark" ? "#34d399" : "#059669",
    },
    alertError: {
      background: theme === "dark"
        ? "rgba(239, 68, 68, 0.15)"
        : "rgba(239, 68, 68, 0.1)",
      border: `1px solid ${theme === "dark" ? "rgba(239, 68, 68, 0.3)" : "rgba(239, 68, 68, 0.2)"}`,
      color: theme === "dark" ? "#f87171" : "#dc2626",
    },
    gridTwo: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
      gap: "16px",
    },
    checkboxLabel: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "10px 12px",
      cursor: "pointer",
      borderRadius: "8px",
      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      background: "transparent",
      userSelect: "none",
      WebkitTapHighlightColor: "transparent",
    },
    checkbox: {
      width: "18px",
      height: "18px",
      cursor: "pointer",
    },
    sectionBox: {
      padding: "20px",
      borderRadius: "12px",
      border: theme === "dark"
        ? "1px solid rgba(148, 163, 184, 0.15)"
        : "1px solid rgba(15, 23, 42, 0.05)",
      background: theme === "dark"
        ? "rgba(15, 23, 42, 0.4)"
        : "rgba(248, 250, 252, 0.7)",
      marginBottom: "20px",
      transition: "all 0.2s ease",
    },
    sectionTitle: {
      fontWeight: "700",
      fontSize: "14px",
      marginBottom: "12px",
      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
    },
    companyGroup: {
      marginBottom: "20px",
      paddingBottom: "16px",
      borderBottom: theme === "dark"
        ? "1px solid rgba(148, 163, 184, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.04)",
    },
    companyTitle: {
      fontSize: "13px",
      fontWeight: "600",
      color: theme === "dark" ? "#cbd5e1" : "#475569",
      marginBottom: "8px",
      paddingBottom: "6px",
      borderBottom: theme === "dark"
        ? "1px solid rgba(148, 163, 184, 0.1)"
        : "1px solid rgba(15, 23, 42, 0.05)",
    },
    tipText: {
      fontSize: "12px",
      color: theme === "dark" ? "#94a3b8" : "#64748b",
      fontStyle: "italic",
      marginTop: "10px",
    },
    emptyState: {
      textAlign: "center",
      padding: "40px 20px",
      color: theme === "dark" ? "#94a3b8" : "#64748b",
    },
    loadingState: {
      textAlign: "center",
      padding: "20px",
      color: theme === "dark" ? "#cbd5e1" : "#64748b",
    },
  };

  return (
    <div>
      {/* Header - Welcome Section */}
      <div style={styles.card}>
        <h2 style={styles.title}>{t.pageTitle}</h2>
        <button
          style={{ ...styles.button, ...styles.primaryButton, marginTop: "16px" }}
          onClick={() => {
            if (!selectedCompany.client || !selectedCompany.company) {
              setError(t.selectCompany || "Please select a company before creating an admin level.");
              return;
            }
            setShowCreateModal(true);
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          <Plus size={18} /> {t.createAdminLevel}
        </button>
      </div>

      {/* Stats Cards */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "16px",
        marginBottom: "24px",
      }}>
        <div style={{
          ...styles.card,
          padding: "20px",
          borderLeft: `4px solid ${theme === "dark" ? "#3b82f6" : "#60a5fa"}`,
        }}>
          <p style={{ margin: "0 0 8px 0", color: theme === "dark" ? "#94a3b8" : "#64748b", fontSize: "13px", fontWeight: "600" }}>
            {t.adminLevels}
          </p>
          <p style={{ margin: "0", fontSize: "28px", fontWeight: "700", color: theme === "dark" ? "#f1f5f9" : "#1e293b" }}>
            {adminLevels.length}
          </p>
        </div>
      </div>

      {/* Company Selection Card */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>{t.selectCompany}</h3>
        <div style={styles.gridTwo}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>{t.clientName}</label>
            <div style={{ ...styles.input, display: "flex", alignItems: "center", gap: "8px", cursor: "default" }}>
              {selectedCompany.client || (companiesLoading ? t.loadingCompanies : "-")}
            </div>
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>{t.workingCompany}</label>
            <ReusableDropdown
              items={(companies || [])
                .filter(c => String(c?.clientName || "").trim() === String(selectedCompany.client || "").trim() && c?.companyName)
                .map(c => String(c.companyName).trim())}
              value={companySearchValue}
              onChange={setCompanySearchValue}
              onSelect={(companyName) => {
                const trimmedCompany = String(companyName || "").trim();
                setCompanySearchValue(trimmedCompany);
                setSelectedCompany({ ...selectedCompany, company: trimmedCompany });
              }}
              placeholder={t.selectCompany2}
              theme={theme}
              language={language}
              maxItems={10}
              getItemLabel={(item) => item}
              getItemKey={(item) => item}
              disabled={!selectedCompany.client}
            />
          </div>
        </div>
        <button
          style={{ ...styles.button, ...styles.primaryButton, marginTop: "16px" }}
          onClick={() => {
            const { client, company } = getTrimmedCompanyContext(
              selectedCompany.client,
              selectedCompany.company
            );
            fetchAdminLevels(client, company);
          }}
          disabled={!String(selectedCompany.client || "").trim() || !String(selectedCompany.company || "").trim() || companiesLoading}
        // onMouseEnter={(e) => {
        //   if (!e.currentTarget.disabled) {
        //     e.currentTarget.style.transform = "translateY(-2px)";
        //     e.currentTarget.style.boxShadow = "0 6px 20px rgba(59, 130, 246, 0.5)";
        //   }
        // }}
        // onMouseLeave={(e) => {
        //   e.currentTarget.style.transform = "translateY(0)";
        //   e.currentTarget.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.4)";
        // }}
        >
          {companiesLoading ? t.loadingCompanies : t.loadAdminLevels}
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div style={{ ...styles.alert, ...styles.alertError }}>
          <AlertCircle size={20} /> {error}
        </div>
      )}
      {success && (
        <div style={{ ...styles.alert, ...styles.alertSuccess }}>
          <CheckCircle size={20} /> {success}
        </div>
      )}

      {/* Admin Levels List */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>{t.adminLevels}</h3>
        {loading ? (
          <div style={styles.loadingState}>{t.loading}</div>
        ) : adminLevels.length === 0 ? (
          <div style={styles.emptyState}>{t.noAdminLevels}</div>
        ) : (
          adminLevels.map((level) => (
            <div
              key={level.id}
              style={styles.levelCard}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = theme === "dark"
                  ? "0 8px 24px rgba(0, 0, 0, 0.3)"
                  : "0 8px 24px rgba(0, 0, 0, 0.08)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = theme === "dark"
                  ? "0 2px 8px rgba(0, 0, 0, 0.2)"
                  : "0 2px 8px rgba(0, 0, 0, 0.04)";
              }}
            // onMouseEnter={(e) => {
            //   e.currentTarget.style.transform = "translateY(-2px)";
            //   e.currentTarget.style.boxShadow = theme === "dark" 
            //     ? "0 8px 24px rgba(0, 0, 0, 0.4)" 
            //     : "0 8px 24px rgba(15, 23, 42, 0.12)";
            // }}
            // onMouseLeave={(e) => {
            //   e.currentTarget.style.transform = "translateY(0)";
            //   e.currentTarget.style.boxShadow = "none";
            // }}
            >
              <div style={styles.levelHeader} onClick={() => setExpandedLevel(expandedLevel === level.id ? null : level.id)}>
                <div style={styles.levelInfo}>
                  <div style={styles.levelName}>{level.name}</div>
                  <p style={styles.levelDesc}>{level.description}</p>
                </div>
                <div style={styles.levelActions}>
                  <button
                    style={styles.iconButton}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = theme === "dark" ? "rgba(239, 68, 68, 0.15)" : "rgba(239, 68, 68, 0.1)";
                      e.currentTarget.style.transform = "scale(1.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteAdminLevel(level.id);
                    }}
                  // onMouseEnter={(e) => {
                  //   e.currentTarget.style.background = "rgba(239, 68, 68, 0.1)";
                  // }}
                  // onMouseLeave={(e) => {
                  //   e.currentTarget.style.background = "transparent";
                  // }}
                  >
                    <Trash2 size={18} style={{ color: "#ef4444" }} />
                  </button>
                  <ChevronDown
                    size={22}
                    style={{
                      transform: expandedLevel === level.id ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.3s ease",
                      color: theme === "dark" ? "#94a3b8" : "#64748b",
                    }}
                  />
                </div>
              </div>

              {/* Expanded Content */}
              {expandedLevel === level.id && (
                <div style={styles.expandedContent}>
                  <div style={styles.actionButtons}>
                    <button
                      style={{ ...styles.button, ...styles.secondaryButton, ...styles.smallButton }}
                      onClick={() => openVisibilityControl(level)}
                    >
                      <Eye size={16} /> {t.visibilityControl}
                    </button>
                    <button
                      style={{ ...styles.button, ...styles.secondaryButton, ...styles.smallButton }}
                      onClick={() => openAssignEmployees(level)}
                    >
                      <Users size={16} /> {t.assignEmployees}
                    </button>
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                    <h5 style={styles.cardTitle}>{t.modulesFeatures}</h5>
                    <button
                      style={{ ...styles.button, ...styles.primaryButton, ...styles.smallButton }}
                      onClick={() => {
                        setSelectedLevelForModule(level.id);
                        setShowModuleModal(true);
                      }}
                    >
                      <Plus size={16} /> {t.addModule}
                    </button>
                  </div>

                  {level.modulePermissions && level.modulePermissions.length > 0 ? (
                    level.modulePermissions.map((module) => (
                      <div key={module.id} style={styles.moduleCard}>
                        <div style={styles.moduleHeader}>
                          <div style={styles.moduleName}>
                            <button
                              style={{
                                ...styles.toggle,
                                ...(module.isEnabled && styles.toggleActive),
                              }}
                              onClick={() => handleSetModulePermission(level.id, module.module, !module.isEnabled)}
                            >
                              <div style={{
                                ...styles.toggleButton,
                                left: language === "ar"
                                  ? (module.isEnabled ? "2px" : "24px")
                                  : (module.isEnabled ? "24px" : "2px"),
                              }} />
                            </button>
                            <span style={{ fontWeight: "600", fontSize: "15px" }}>
                              {AVAILABLE_MODULES.find(m => m.name === module.module)?.label || module.module}
                            </span>
                          </div>
                          <button
                            style={{ ...styles.button, ...styles.secondaryButton, ...styles.smallButton }}
                            onClick={() => {
                              setSelectedModuleForFeature(module.id);
                              setShowFeatureModal(true);
                            }}
                          >
                            <Plus size={14} /> {t.addFeature}
                          </button>
                        </div>

                        {/* Features */}
                        <div style={styles.featuresList}>
                          {module.featurePermissions && module.featurePermissions.length > 0 ? (
                            module.featurePermissions.map((feature) => (
                              <div key={feature.id} style={styles.featureItem}>
                                <button
                                  style={{
                                    ...styles.toggle,
                                    ...styles.smallToggle,
                                    ...(feature.isEnabled && styles.toggleActive),
                                  }}
                                  onClick={() =>
                                    handleSetFeaturePermission(module.id, feature.featureName, !feature.isEnabled)
                                  }
                                >
                                  <div style={{
                                    ...styles.toggleButton,
                                    ...styles.smallToggleButton,
                                    left: language === "ar"
                                      ? (feature.isEnabled ? "2px" : "18px")
                                      : (feature.isEnabled ? "18px" : "2px"),
                                  }} />
                                </button>
                                <span>{getFeatureLabel(feature.featureName)}</span>
                              </div>
                            ))
                          ) : (
                            <p style={{ margin: 0, fontSize: "13px", color: theme === "dark" ? "#94a3b8" : "#64748b" }}>
                              {t.noFeatures}
                            </p>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={styles.emptyState}>{t.noModules}</div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Create Admin Level Modal */}
      {showCreateModal && (
        <div style={styles.modal} onClick={() => setShowCreateModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{t.createAdminLevel}</h2>
              <button
                style={styles.iconButton}
                onClick={() => setShowCreateModal(false)}
              >
                <X size={24} color={theme === "dark" ? "#94a3b8" : "#64748b"} />
              </button>
            </div>

            <form onSubmit={handleCreateAdminLevel}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>{t.name}</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t.namePlaceholder}
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>{t.description}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t.descPlaceholder}
                  style={{ ...styles.input, ...styles.textarea }}
                />
              </div>

              <div style={styles.modalFooter}>
                <button
                  type="submit"
                  disabled={loading}
                  style={{ ...styles.button, ...styles.primaryButton }}
                >
                  <Save size={18} /> {t.create}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  style={{ ...styles.button, ...styles.secondaryButton }}
                >
                  {t.cancel}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Module Modal */}
      {showModuleModal && (
        <div style={styles.modal} onClick={() => setShowModuleModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{t.addModule}</h2>
              <button
                style={styles.iconButton}
                onClick={() => setShowModuleModal(false)}
              >
                <X size={24} color={theme === "dark" ? "#94a3b8" : "#64748b"} />
              </button>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>{t.selectModule}</label>
              <ReusableDropdown
                items={AVAILABLE_MODULES}
                value={selectedModuleName}
                onChange={setSelectedModuleName}
                onSelect={(mod) => setSelectedModuleName(mod.name)}
                placeholder={t.selectModulePlaceholder}
                theme={theme}
                language={language}
                maxItems={20}
                getItemKey={(item) => item.name}
                getItemLabel={(item) => item.label}
              />
            </div>

            <div style={styles.modalFooter}>
              <button
                onClick={handleAddModule}
                disabled={!selectedModuleName}
                style={{ ...styles.button, ...styles.primaryButton }}
                onMouseEnter={(e) => !selectedModuleName && (e.currentTarget.style.opacity = "0.6")}
                onMouseLeave={(e) => !selectedModuleName && (e.currentTarget.style.opacity = "1")}
              >
                <Plus size={18} /> {t.addModule}
              </button>
              <button
                onClick={() => setShowModuleModal(false)}
                style={{ ...styles.button, ...styles.secondaryButton }}
              >
                {t.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Feature Modal */}
      {showFeatureModal && (
        <div style={styles.modal} onClick={() => setShowFeatureModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{t.addFeature}</h2>
              <button
                style={styles.iconButton}
                onClick={() => setShowFeatureModal(false)}
              >
                <X size={24} color={theme === "dark" ? "#94a3b8" : "#64748b"} />
              </button>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>{t.selectFeature}</label>
              <ReusableDropdown
                items={selectedModuleForFeature
                  ? (AVAILABLE_FEATURES[
                    adminLevels
                      .flatMap(l => l.modulePermissions || [])
                      .find(m => m.id === selectedModuleForFeature)?.module
                  ] || []).map(feat => ({ name: feat, label: getFeatureLabel(feat) }))
                  : []
                }
                value={selectedFeatureName}
                onChange={setSelectedFeatureName}
                onSelect={(feat) => setSelectedFeatureName(feat.name)}
                placeholder={t.selectFeaturePlaceholder}
                theme={theme}
                language={language}
                getItemKey={(item) => item.name}
                getItemLabel={(item) => item.label}
              />
            </div>

            <div style={styles.modalFooter}>
              <button
                onClick={handleAddFeature}
                disabled={!selectedFeatureName}
                style={{ ...styles.button, ...styles.primaryButton }}
                onMouseEnter={(e) => !selectedFeatureName && (e.currentTarget.style.opacity = "0.6")}
                onMouseLeave={(e) => !selectedFeatureName && (e.currentTarget.style.opacity = "1")}
              >
                <Plus size={18} /> {t.addFeature}
              </button>
              <button
                onClick={() => setShowFeatureModal(false)}
                style={{ ...styles.button, ...styles.secondaryButton }}
              >
                {t.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Visibility Control Modal */}
      {showVisibilityModal && (
        <div style={styles.modal} onClick={() => setShowVisibilityModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{t.visibilityControlTitle}</h2>
              <button
                style={styles.iconButton}
                onClick={() => setShowVisibilityModal(false)}
              >
                <X size={24} color={theme === "dark" ? "#94a3b8" : "#64748b"} />
              </button>
            </div>

            <p style={{ marginTop: 0, marginBottom: "20px", fontSize: "13px", color: theme === "dark" ? "#94a3b8" : "#64748b" }}>
              {t.visibilityDesc}
            </p>

            <input
              value={visibilitySearch}
              onChange={(e) => setVisibilitySearch(e.target.value)}
              placeholder={t.searchUsers}
              style={{ ...styles.input, marginBottom: "20px" }}
            />

            {visibilityRulesLoading ? (
              <div style={styles.loadingState}>{t.loadingVisibility}</div>
            ) : (
              <div>
                {/* Companies */}
                <div style={styles.sectionBox}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={styles.sectionTitle}>{t.companies}</div>
                    <div>
                      <label style={styles.checkboxLabel}>
                        <DotCheckbox
                          checked={
                            (getCompaniesForClient(selectedCompany.client) || []).length > 0 &&
                            (getCompaniesForClient(selectedCompany.client) || []).every((c) =>
                              selectedVisibilityCompanies.includes(c)
                            )
                          }
                          onChange={toggleSelectAllCompanies}
                        />
                        <span style={{ fontSize: "13px" }}>{t.selectAll}</span>
                      </label>
                    </div>
                  </div>

                  {(getCompaniesForClient(selectedCompany.client) || []).map((companyName) => (
                    <label
                      key={companyName}
                      style={styles.checkboxLabel}
                      onMouseEnter={(e) => e.currentTarget.style.background = theme === "dark" ? "rgba(148, 163, 184, 0.08)" : "rgba(15, 23, 42, 0.03)"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                    >
                      <DotCheckbox
                        checked={selectedVisibilityCompanies.includes(companyName)}
                        onChange={() => handleToggleCompany(companyName)}
                      />
                      <span>{companyName}</span>
                    </label>
                  ))}
                  {getCompaniesForClient(selectedCompany.client).length === 0 && (
                    <p style={{ margin: 0, fontSize: "13px", color: theme === "dark" ? "#94a3b8" : "#64748b" }}>
                      {t.noCompanies}
                    </p>
                  )}
                </div>

                {/* Departments */}
                <div style={styles.sectionBox}>
                  <div style={styles.sectionTitle}>{t.departments}</div>
                  {selectedVisibilityCompanies.length === 0 ? (
                    <p style={{ margin: 0, fontSize: "13px", color: theme === "dark" ? "#94a3b8" : "#64748b" }}>
                      {t.selectAtLeastOne}
                    </p>
                  ) : (
                    selectedVisibilityCompanies.map((companyName) => (
                      <div key={companyName} style={styles.companyGroup}>
                        <div style={styles.companyTitle}>{companyName}</div>
                        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "8px" }}>
                          <label style={styles.checkboxLabel}>
                            <DotCheckbox
                              checked={
                                (departmentsByCompany[companyName] || []).length > 0 &&
                                (departmentsByCompany[companyName] || []).every((d) =>
                                  (selectedDepartmentsByCompany[companyName] || []).includes(d)
                                )
                              }
                              onChange={() => toggleSelectAllDepartments(companyName)}
                            />
                            <span style={{ fontSize: "13px" }}>{t.selectAll}</span>
                          </label>
                        </div>

                        {(departmentsByCompany[companyName] || []).map((dept) => (
                          <label
                            key={`${companyName}__${dept}`}
                            style={styles.checkboxLabel}
                            onMouseEnter={(e) => e.currentTarget.style.background = theme === "dark" ? "rgba(148, 163, 184, 0.08)" : "rgba(15, 23, 42, 0.03)"}
                            onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                          >
                            <DotCheckbox
                              checked={(selectedDepartmentsByCompany[companyName] || []).includes(dept)}
                              onChange={() => handleToggleDepartment(companyName, dept)}
                            />
                            <span>{dept}</span>
                          </label>
                        ))}
                        {(departmentsByCompany[companyName] || []).length === 0 && (
                          <p style={{ margin: 0, fontSize: "12px", color: theme === "dark" ? "#94a3b8" : "#64748b" }}>
                            {t.noDepartments}
                          </p>
                        )}
                      </div>
                    ))
                  )}
                  <p style={styles.tipText}>{t.departmentTip}</p>
                </div>

                {/* Users */}
                <div style={styles.sectionBox}>
                  <div style={styles.sectionTitle}>{t.users}</div>
                  <p style={{ ...styles.tipText, marginTop: 0, marginBottom: "12px" }}>{t.usersTip}</p>

                  {selectedVisibilityCompanies.map((companyName) => {
                    const list = usersByCompany[companyName] || [];
                    const filtered = list.filter((u) => {
                      if (!visibilitySearch) return true;
                      const v = visibilitySearch.toLowerCase();
                      return safeUserLabel(u).toLowerCase().includes(v);
                    });

                    // Group by department
                    const grouped = filtered.reduce((acc, u) => {
                      const dept = u?.employee?.employeeType || "-";
                      if (!acc[dept]) acc[dept] = [];
                      acc[dept].push(u);
                      return acc;
                    }, {});

                    const deptKeys = Object.keys(grouped);

                    return (
                      <div key={`${companyName}__users`} style={styles.companyGroup}>
                        <div style={styles.companyTitle}>{companyName}</div>
                        <div style={{ maxHeight: "220px", overflowY: "auto" }}>
                          {deptKeys.length === 0 ? (
                            <p style={{ margin: 0, fontSize: "12px", color: theme === "dark" ? "#94a3b8" : "#64748b" }}>
                              {t.noUsers}
                            </p>
                          ) : (
                            deptKeys.map((dept) => {
                              const deptList = grouped[dept] || [];
                              const deptIds = deptList.map((u) => u.id);
                              const deptAllSelected = deptIds.length > 0 && deptIds.every((id) => (selectedUsersByCompany[companyName] || []).includes(id));

                              return (
                                <div key={`${companyName}__dept__${dept}`} style={{ marginBottom: "12px" }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "6px 0" }}>
                                    <div style={{ fontSize: "13px", fontWeight: 700 }}>{dept}</div>
                                    <label style={styles.checkboxLabel}>
                                      <DotCheckbox checked={deptAllSelected} onChange={() => toggleSelectAllDepartmentUsers(companyName, dept)} />
                                      <span style={{ fontSize: "13px" }}>{t.selectAll}</span>
                                    </label>
                                  </div>

                                  {deptList.slice(0, 200).map((u) => (
                                    <label key={`${companyName}__u__${u.id}`} style={styles.checkboxLabel}>
                                      <DotCheckbox
                                        checked={(selectedUsersByCompany[companyName] || []).includes(u.id)}
                                        onChange={() => handleToggleUser(companyName, u.id)}
                                      />
                                      <span style={{ fontSize: "12px" }}>{safeUserLabel(u)}</span>
                                    </label>
                                  ))}
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={styles.modalFooter}>
              <button
                onClick={saveVisibilityRules}
                disabled={loading || visibilityRulesLoading || selectedVisibilityCompanies.length === 0}
                style={{
                  ...styles.button,
                  ...styles.primaryButton,
                  opacity: (loading || visibilityRulesLoading || selectedVisibilityCompanies.length === 0) ? 0.6 : 1,
                  cursor: (loading || visibilityRulesLoading || selectedVisibilityCompanies.length === 0) ? "not-allowed" : "pointer",
                }}
                onMouseEnter={(e) => {
                  if (!loading && !visibilityRulesLoading && selectedVisibilityCompanies.length > 0) {
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(46, 124, 186, 0.4)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <Save size={18} /> {t.save}
              </button>
              <button
                onClick={() => setShowVisibilityModal(false)}
                style={{ ...styles.button, ...styles.secondaryButton }}
              >
                {t.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Employees Modal */}
      {showAssignModal && (
        <div style={styles.modal} onClick={() => setShowAssignModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{t.assignToLevel}</h2>
              <button
                style={styles.iconButton}
                onClick={() => setShowAssignModal(false)}
              >
                <X size={24} color={theme === "dark" ? "#94a3b8" : "#64748b"} />
              </button>
            </div>

            <p style={{ marginTop: 0, marginBottom: "20px", fontSize: "13px", color: theme === "dark" ? "#94a3b8" : "#64748b" }}>
              {t.assignDesc} <b>{selectedLevelForAssign?.name}</b>.
            </p>

            <input
              value={assignSearch}
              onChange={(e) => setAssignSearch(e.target.value)}
              placeholder={t.searchByName}
              style={{ ...styles.input, marginBottom: "16px" }}
            />

            {assignLoading ? (
              <div style={styles.loadingState}>{t.loadingUsers}</div>
            ) : (
              <div style={{ maxHeight: "50vh", overflowY: "auto", ...styles.sectionBox }}>
                {assignCandidates
                  .filter((u) => {
                    if (!assignSearch) return true;
                    return safeUserLabel(u).toLowerCase().includes(assignSearch.toLowerCase());
                  })
                  .slice(0, 400)
                  .map((u) => (
                    <label key={`assign__${u.id}`} style={styles.checkboxLabel}>
                      <DotCheckbox
                        checked={assignSelectedUserIds.includes(u.id)}
                        onChange={() => toggleAssignUser(u.id)}
                      />
                      <span style={{ fontSize: "13px" }}>{safeUserLabel(u)}</span>
                    </label>
                  ))}
              </div>
            )}

            <div style={styles.modalFooter}>
              <button
                onClick={saveAssignments}
                disabled={assignLoading || assignSelectedUserIds.length === 0}
                style={{ ...styles.button, ...styles.primaryButton }}
              >
                <Save size={18} /> {t.saveAssignments}
              </button>
              <button
                onClick={() => setShowAssignModal(false)}
                style={{ ...styles.button, ...styles.secondaryButton }}
              >
                {t.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <AlertModal
        open={showDeleteConfirm}
        title={t.deleteConfirm}
        message={language === "ar" ? "سيتم حذف هذا المستوى الإداري بشكل نهائي ولا يمكن التراجع عنه." : "This admin level will be permanently deleted and cannot be undone."}
        variant="error"
        showCancel={true}
        onConfirm={confirmDeleteAdminLevel}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDeletingLevelId(null);
        }}
        theme={theme}
      />
    </div>
  );
};

export default AdminLevelConfigurationPage;